import { memo } from 'react';

import { List } from '../../../List/List';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import { usePlaceholders } from '../../usePlaceholders';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { AssetItem } from '../../common/AssetItem/AssetItem';
import { useAssetItemTracking } from '../../common/useAssetItemTracking';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { AssetListComponent } from '@vgtv/api-client/lib/component/list';
import type { FC } from 'react';

import * as styles from './VerticalAssetsList.module.scss';

interface VerticalAssetsListProps {
  componentConfig: AssetListComponent;
  pageComponentIndex: number;
}

const VerticalAssetsListBase: FC<VerticalAssetsListProps> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isFetched } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpAssets,
  });

  const assets = useComponentAssets(componentConfig.id);

  const placeholders = usePlaceholders(isFetched);

  const { trackAssetClick } = useAssetItemTracking({
    elementType: 'VerticalAssetsList',
    header: componentConfig.header,
    pageComponentIndex,
  });

  if (isFetched && assets.length === 0) {
    return null;
  }

  return (
    <List header={componentConfig.header} className={styles.list}>
      {[
        ...placeholders.map((item, index) => (
          <Placeholder type={placeholderTypes.assetVertical} key={index} />
        )),
        ...assets.map((asset: SvpAssetAdditional, index: number) => {
          return (
            <AssetItem
              key={asset.id}
              asset={asset}
              componentConfig={componentConfig}
              onClick={() => trackAssetClick(asset, index)}
            />
          );
        }),
      ]}
    </List>
  );
};

export const VerticalAssetsList = memo(VerticalAssetsListBase);
